/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearServiceAddressFormErrors,
  fetchContactInfo,
  selectServiceAddressFieldErrors,
  selectServiceAddressFormError,
  updateServiceAddress,
} from '../../../../redux/slices/billingSlice/billingSlice';

function ServiceAddressModal({
  show,
  handleClose,
  serviceAddress = {},
  countryList,
}) {
  const dispatch = useDispatch();

  const fieldErrors = useSelector(selectServiceAddressFieldErrors);
  const formError = useSelector(selectServiceAddressFormError);
  // const loading = useSelector(selectBillingLoading);

  const [form, setForm] = useState({
    streetLine1: '',
    streetLine2: '',
    locality: '',
    state: '',
    postalCode: '',
    country: '',
    id: serviceAddress.id,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;

    // populate form
    setForm({
      streetLine1: serviceAddress.streetLine1 || '',
      streetLine2: serviceAddress.streetLine2 || '',
      locality: serviceAddress.locality || '',
      state: serviceAddress.state || '',
      postalCode: serviceAddress.postalCode || '',
      country: serviceAddress.country || '',
      id: serviceAddress.id,
    });

    // clear previous errors when modal opens
    dispatch(clearServiceAddressFormErrors());
  }, [show]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await dispatch(updateServiceAddress(form)).unwrap();
      dispatch(fetchContactInfo());
      setSubmitting(false);
      handleClose();
    } catch (err) {
      // errors are already handled in Redux
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Service Address</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted">
          This is the physical location where the service is consumed.
        </p>

        {/* FORM LEVEL ERROR */}
        {formError && (
          <div className="alert alert-danger">
            {formError}
          </div>
        )}

        {/* Street */}
        <div className="form-group">
          <label>Street Address</label>

          <input
            className={`form-control mb-2 ${fieldErrors.streetLine1 ? 'is-invalid' : ''}`}
            placeholder="Line 1"
            value={form.streetLine1}
            onChange={(e) => handleChange('streetLine1', e.target.value)}
          />
          {fieldErrors.streetLine1 && (
            <div className="invalid-feedback">
              {fieldErrors.streetLine1}
            </div>
          )}

          <input
            className="form-control"
            placeholder="Line 2"
            value={form.streetLine2}
            onChange={(e) => handleChange('streetLine2', e.target.value)}
          />
        </div>

        {/* City */}
        <div className="form-group">
          <label>City</label>
          <input
            className={`form-control ${fieldErrors.locality ? 'is-invalid' : ''}`}
            value={form.locality}
            onChange={(e) => handleChange('locality', e.target.value)}
          />
          {fieldErrors.locality && (
            <div className="invalid-feedback">
              {fieldErrors.locality}
            </div>
          )}
        </div>

        {/* State */}
        <div className="form-group">
          <label>Province / State</label>
          <input
            className={`form-control ${fieldErrors.state ? 'is-invalid' : ''}`}
            value={form.state}
            onChange={(e) => handleChange('state', e.target.value)}
          />
          {fieldErrors.state && (
            <div className="invalid-feedback">
              {fieldErrors.state}
            </div>
          )}
        </div>

        {/* Zip */}
        <div className="form-group">
          <label>Zip / Postal Code</label>
          <input
            className={`form-control ${fieldErrors.postalCode ? 'is-invalid' : ''}`}
            value={form.postalCode}
            onChange={(e) => handleChange('postalCode', e.target.value)}
          />
          {fieldErrors.postalCode && (
            <div className="invalid-feedback">
              {fieldErrors.postalCode}
            </div>
          )}
        </div>

        {/* Country */}
        <div className="form-group">
          <label>Country</label>

          <select
            className={`form-control ${fieldErrors.country ? 'is-invalid' : ''}`}
            value={form.country}
            onChange={(e) => handleChange('country', e.target.value)}
          >
            <option value="">— choose —</option>

            {countryList.map((country) => (
              <option
                key={country.crmId}
                value={String(country.abbreviation)}
              >
                {country.name}
              </option>
            ))}
          </select>

          {fieldErrors.country && (
          <div className="invalid-feedback">
            {fieldErrors.country}
          </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Apply Changes'}
        </button>

        <button
          type="button"
          className="btn btn-link"
          onClick={handleClose}
        >
          cancel
        </button>
      </Modal.Footer>
    </Modal>
  );
}

ServiceAddressModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  serviceAddress: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    streetLine1: PropTypes.string,
    streetLine2: PropTypes.string,
    locality: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
  }),
  countryList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    crmId: PropTypes.number,
    code: PropTypes.number,
    name: PropTypes.string,
    abbreviation: PropTypes.string,
  })).isRequired,
};

export default ServiceAddressModal;
